import {
  MarketField,
  MarketInputText,
  MarketHeader,
  MarketButton,
  MarketToaster,
  MarketToast,
} from '@market/react';
import BaseModal from 'components/modals/BaseModal';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SendReferralRequest } from 'rpc/model/squareup/rewards/service';
import {
  useSendReferralMutation,
  SEND_REWARD_RESPONSE_ERRORS,
} from 'store/query/api-extensions/rewardsRpcs';
import useValidation from 'utils/custom-react-hooks/useValidation';
import { isValidEmail } from 'utils/identifiers';
import { buyerportalCdpClient } from 'services/tracking/cdp/clients/buyerportal';
import { sendReferralEmailProperties } from 'services/tracking/cdp/events/referrals';

interface ShareLinkModalProps {
  onClose: () => void;
  rewardCodeId: number;
}

export default function ShareLinkModal({
  onClose,
  rewardCodeId,
}: ShareLinkModalProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState<string>('');
  const [
    sendReferral,
    {
      error: sendReferralError,
      isError: didSendReferralError,
      isSuccess: didSendReferralSucceed,
      isLoading: isSendReferralLoading,
    },
  ] = useSendReferralMutation({});
  const [isEmailInvalid, resetEmailValidation, validateEmail] = useValidation(
    (email) => isValidEmail(email)
  );

  const getErrorForMessage = () => {
    if (!sendReferralError) {
      return t('refer.referABusiness.failed.genericError');
    }

    const errorMessage =
      'message' in sendReferralError ? sendReferralError.message : undefined;

    switch (errorMessage) {
      case SEND_REWARD_RESPONSE_ERRORS.DUPLICATE_REFERRAL: {
        return t('refer.referABusiness.failed.duplicate');
      }
      case SEND_REWARD_RESPONSE_ERRORS.EXISTING_MERCHANT: {
        return t('refer.referABusiness.failed.existing');
      }
      case SEND_REWARD_RESPONSE_ERRORS.MISSING_DATA: {
        return t('refer.referABusiness.failed.invalidEmail');
      }
      case SEND_REWARD_RESPONSE_ERRORS.INVALID_REWARD_CODE: {
        return t('refer.referABusiness.failed.invalidRewardCode');
      }
      default: {
        return t('refer.referABusiness.failed.genericError');
      }
    }
  };

  const sendEmailInvite = async () => {
    const valid = validateEmail(email);
    if (!valid) {
      return;
    }

    const request = SendReferralRequest.create({
      emailAddress: email,
      emailBody: '',
      rewardCodeId,
    });

    try {
      await sendReferral(request).unwrap();
      buyerportalCdpClient.track(
        'Send Referral Email',
        sendReferralEmailProperties(true)
      );
    } catch (error) {
      buyerportalCdpClient.track(
        'Send Referral Email',
        sendReferralEmailProperties(false, (error as Error).message)
      );
    }
  };

  const modalTitle = didSendReferralSucceed
    ? t('refer.referABusiness.success.title')
    : t('refer.referABusiness.title');

  const modalDescription = didSendReferralSucceed
    ? t('refer.referABusiness.success.description')
    : t('refer.referABusiness.description');

  return (
    <>
      <BaseModal
        titleElementId="share-link-modal"
        onClose={onClose}
        className="top-0"
      >
        <MarketHeader>
          <h2 id="share-link-modal-title">{modalTitle}</h2>
          <p id="share-link-modal-description">{modalDescription}</p>
        </MarketHeader>
        {!didSendReferralSucceed && (
          <>
            <MarketField
              className="mt-6"
              invalid={isEmailInvalid}
              aria-labelledby={'email-label'}
            >
              <MarketInputText
                type={'email'}
                value={email}
                invalid={isEmailInvalid}
                onMarketInputValueChange={(e) => {
                  const email = e.detail.value;
                  setEmail(email);
                  resetEmailValidation();
                }}
                required={true}
              >
                <label id="email-label">
                  {t('refer.referABusiness.label')}
                </label>
              </MarketInputText>
              <small slot={'error'}>
                {t('common.message.validation.email')}
              </small>
            </MarketField>
            <MarketButton
              rank="primary"
              onClick={sendEmailInvite}
              className="mt-6"
              disabled={isEmailInvalid}
              isLoading={isSendReferralLoading}
            >
              {t('refer.sendInvite.button')}
            </MarketButton>
          </>
        )}
      </BaseModal>

      {didSendReferralError && (
        <>
          <MarketToaster>
            <MarketToast variant="critical">{getErrorForMessage()}</MarketToast>
          </MarketToaster>
        </>
      )}
    </>
  );
}
